// src/components/blog/components/BlogImage.tsx

import Image, { type ImageProps } from 'next/image'
import { cn } from '~/lib/utils'
import { withDevCacheBust } from '~/lib/utils/url'

/**
 * Props for the BlogImage component
 * @interface BlogImageProps
 * @property {string} src - Source URL of the image
 * @property {string} alt - Alt text for accessibility and SEO
 * @property {string} [className] - Optional CSS classes for styling
 * @property {boolean} [priority=false] - Whether to prioritize loading this image
 * @property {'16/9' | '4/3' | '1/1' | '3/2'} [aspectRatio] - Aspect ratio for the image container
 * @property {string} [sizes] - Responsive image sizes attribute
 * @property {number} [width] - Width of the image, defaults to 1200
 * @property {number} [height] - Height of the image, defaults to 675 (16:9 ratio)
 * @property {boolean} [fill] - Whether the image should fill its container
 * @property {boolean} [blur] - Whether to enable the blur placeholder
 * @property {string} [blurDataURL] - Custom data URL for the blur placeholder
 * @property {'preview' | 'og' | 'hero'} [type] - Type of image (not forwarded to the underlying Image)
 * @property {boolean} [useLazyLoading] - Whether to use enhanced lazy loading
 */
export interface BlogImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  src: string
  alt: string
  className?: string
  aspectRatio?: '16/9' | '4/3' | '1/1' | '3/2'
  sizes?: string
  width?: number
  height?: number
  fill?: boolean
  blur?: boolean
  blurDataURL?: string
  _type?: 'preview' | 'og' | 'hero'
}

/**
 * Get image quality based on width/size.
 */
function getQualityForDimensions(width: number): number {
  if (width <= 400) return 70 // Lower quality for smaller images
  if (width <= 800) return 75
  if (width <= 1200) return 80
  return 85 // Cap quality at 85 for largest images
}

/**
 * Fallback placeholder for unavailable images.
 */
function FallbackImage({
  alt,
  width,
  height,
}: {
  alt: string
  width: number
  height: number
}): JSX.Element {
  return (
    <div
      className="flex items-center justify-center w-full h-full bg-gray-200 dark:bg-gray-700 rounded-2xl"
      style={{
        minHeight: height,
        minWidth: width,
        aspectRatio: width && height ? `${width} / ${height}` : undefined,
      }}
      aria-label={alt}
      role="img"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="w-1/3 h-1/3 text-gray-400"
        aria-hidden="true"
      >
        <rect width="100%" height="100%" fill="#e5e7eb" />
        <g>
          <circle
            cx={width / 2}
            cy={height / 2}
            r={Math.min(width, height) / 6}
            fill="#cbd5e1"
          />
          <rect
            x={width * 0.2}
            y={height * 0.7}
            width={width * 0.6}
            height={height * 0.15}
            fill="#cbd5e1"
            rx={height * 0.03}
          />
        </g>
      </svg>
      <span className="sr-only">{alt}</span>
    </div>
  )
}

/**
 * BlogImage - Server Component for optimized blog images with fallback.
 */
export function BlogImage({
  src,
  alt,
  className,
  aspectRatio,
  width = 1200,
  height = 675,
  sizes = '(min-width: 1920px) 1200px, (min-width: 1280px) 900px, (min-width: 1024px) 700px, (min-width: 768px) 600px, 100vw',
  fill = false,
  blur = false,
  blurDataURL,
  _type, // Destructured but not used directly
  ...props
}: BlogImageProps): JSX.Element {
  // Determine quality based on dimensions
  const quality = getQualityForDimensions(width)

  // Generate blur placeholder if needed
  const defaultBlurDataURL = `data:image/svg+xml;base64,${Buffer.from(
    `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#e5e7eb"/></svg>`
  ).toString('base64')}`

  // Try to render the image, fallback to placeholder if error
  // Next.js 15: Use onError prop to show fallback via the new error boundary
  // But for server components, we can't handle runtime errors, so we use a fallback if src is missing or invalid
  const isValidSrc = typeof src === 'string' && src.length > 0

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800",
        className
      )}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {isValidSrc ? (
        <Image
          src={withDevCacheBust(src)}
          alt={alt}
          priority={false}
          sizes={sizes}
          className="object-cover w-full h-full transition duration-300 ease-in-out group-hover:scale-105"
          {...(!fill ? { width, height } : { fill: true })}
          {...(blur ? { placeholder: 'blur' as const, blurDataURL: blurDataURL ?? defaultBlurDataURL } : {})}
          quality={quality}
          {...props}
          // onError is not supported in server components, so fallback is only for missing/invalid src
        />
      ) : (
        <FallbackImage alt={alt} width={width} height={height} />
      )}
    </div>
  )
}